import { vars, Heading, Stack, Inline } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { Overlay } from '@eti/components';
import { css, cx } from '@eti/styles';
// oxlint-disable-next-line no-restricted-imports -- TODO WEBTECH-1501: Replace old schema object types with operation-specific types.
import type { SubMenuItem } from '@eti/schema-types/schema-objects';
import { useLanguage } from '../../../common/localization/custom-hooks/language';
import LanguageSelectionDropdown from '../../../common/localization/components/LanguageSelectionDropdown';
import SettingsGraphqlContainer from '../../../common/settings/components/SettingsGraphqlContainer';

const drawerStyles = css`
  color: ${vars.colors.header.content};
  padding: 8px;
`;

const drawerHeaderStyles = css`
  background-color: ${vars.colors.header.base};
  color: ${vars.colors.header.content};

  & #modal-title:focus-visible {
    outline: 2px solid ${vars.colors.header.content};
    outline-offset: 2px;
  }
`;

const menuItemStyles = (isSettingsEnabled: boolean, shouldShowMultiLanguage: boolean) => css`
  border-bottom: 1px solid ${vars.colors.divider};
  padding-bottom: 24px;

  &:last-child {
    ${!shouldShowMultiLanguage && !isSettingsEnabled ? 'border-bottom: 0;' : ''}
  }
`;

const linkStyles = css`
  color: ${vars.colors.header.content};
  font-size: 1rem;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid ${vars.colors.header.content};
    outline-offset: 2px;
  }
`;

const subMenuListStyles = css`
  list-style: none;
`;

interface MobileMenuProps {
  isMobileMenuOpen: boolean;
  menu: {
    id: string;
    name: string;
    path: string;
    subMenu: SubMenuItem[];
  }[];
  menuTitleText: string;
  toggleMobileMenu: () => void;
}

const MobileMenu = ({
  isMobileMenuOpen,
  menu,
  menuTitleText,
  toggleMobileMenu,
}: MobileMenuProps) => {
  const { lessThanTwoAvailableLanguages } = useLanguage();
  const { p } = useProperty();
  const { t } = useTranslation();
  const isSettingsEnabled =
    p('IbeClient.Settings.Selection.Country.Enabled') ||
    p('IbeClient.Settings.Selection.Language.Enabled') ||
    p('IbeClient.Settings.Selection.Currency.Enabled');
  const shouldShowMultiLanguage = p('Site.MultiLanguage.Enabled') && !lessThanTwoAvailableLanguages;

  return (
    <Overlay.Drawer
      backgroundColor={vars.colors.header.base}
      className={cx(drawerStyles, { 'mobile-menu-open': isMobileMenuOpen })}
      contentLabel={menuTitleText}
      handleClose={toggleMobileMenu}
      isOpen={isMobileMenuOpen}
      shouldOpenHorizontally
    >
      <Overlay.Header
        className={drawerHeaderStyles}
        dialogCloseButtonAriaLabel={t('SiteMenu.CloseMenu.Label')}
        onClose={toggleMobileMenu}
        title={menuTitleText}
      />
      <Overlay.Content>
        <Stack data-testid="menu" spacing={24}>
          <Stack aria-label={t('ScreenReader.SiteMenu.Navigation.AriaLabel')} as="nav" spacing={24}>
            {menu.map(({ name: subMenuTitle, subMenu }, index) => (
              <Stack
                className={menuItemStyles(shouldShowMultiLanguage, isSettingsEnabled)}
                key={index}
                spacing={24}
              >
                <Heading level={3}>{subMenuTitle}</Heading>
                <Stack as="ul" className={subMenuListStyles} spacing={24}>
                  {subMenu.map(({ name, path, target }) => (
                    <li key={name}>
                      <a
                        className={linkStyles}
                        data-testid={`menu-link-${path.split('/').at(-1)}`}
                        href={path}
                        target={target}
                      >
                        {name}
                      </a>
                    </li>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
          {isSettingsEnabled ? (
            <Inline align="start">
              <SettingsGraphqlContainer componentType="Menu" />
            </Inline>
          ) : (
            shouldShowMultiLanguage && <LanguageSelectionDropdown />
          )}
        </Stack>
      </Overlay.Content>
    </Overlay.Drawer>
  );
};

export default MobileMenu;
